import React from "react";
import useMediaQuery from "@mui/system/useMediaQuery";
import { Typography, useTheme } from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import { BasicModalContent } from "@dreebit-registry/vsm-microservice-components";
import { resolveErrorMessage } from "../../utils/resolveErrorMessage";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import _ from "lodash";
import moment from "moment";
import { ServiceReminderTdas } from "../../graphql/queries/serviceReminderTdas";
import { ResetServiceReminder } from "../../graphql/mutations/resetServiceReminder";
import InstalledBaseTDAForm from "../InstalledBaseTDAForm";
import { isDueDateAfter } from "../../utils/serviceReminderDateValidation";

interface Props {
    open: boolean;
    installedBaseNodeId: string;
    reminderId: string;
    serviceName?: string;
    onClose: () => void;
}

/**
 * Reset = the service was completed. The user confirms the completion date
 * (defaults to today, becomes the new last-service date) and picks the next due date.
 */
const ResetServiceReminderModal = ({
    open,
    installedBaseNodeId,
    reminderId,
    serviceName,
    onClose,
}: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation();

    const [resetServiceReminder] = useMutation(ResetServiceReminder);

    const { data } = useQuery(ServiceReminderTdas, {
        variables: { installedBaseNodeId, typeDefinitionIndex: "SERVICE_REMINDER_RESET" },
        skip: !installedBaseNodeId || !open,
        fetchPolicy: "network-only",
    });

    const attributes = _.get(
        data,
        "serviceReminderList.typeDefinitionAttributeList.typeDefinitionAttributes",
        []
    );

    const initialFormValues = {
        completionDate: moment().format("YYYY-MM-DD"),
    };

    const handleSubmit = (input: any) => {
        if (!isDueDateAfter(input.newDueDate, input.completionDate)) {
            toast.error(t("The next due date must be after the completion date."));
            return;
        }

        return resetServiceReminder({
            variables: { id: reminderId, input },
            refetchQueries: ["ServiceReminderList"],
        })
            .then((res) => {
                if (res.data?.resetServiceReminder?.id) {
                    toast.success(t("Service reminder renewed successfully."));
                    onClose();
                } else {
                    toast.error(t("Error renewing service reminder."));
                }
            })
            .catch((err) => {
                toast.error(resolveErrorMessage(err, t));
            });
    };

    const title = serviceName
        ? `${t("Renew service reminder")} – ${serviceName}`
        : t("Renew service reminder");

    return (
        <Dialog open={open} onClose={onClose} fullScreen={isFullScreen}>
            <BasicModalContent
                onClose={onClose}
                title={<Typography level={"titleSmallLight"}>{title}</Typography>}
            >
                <InstalledBaseTDAForm
                    formStyle={{ paddingTop: "0px" }}
                    onSubmit={handleSubmit}
                    onCancel={onClose}
                    submitButtonText={t("Renew")}
                    attributes={attributes}
                    initialFormValues={initialFormValues}
                />
            </BasicModalContent>
        </Dialog>
    );
};

export default ResetServiceReminderModal;
